import React, { useEffect, useState } from 'react';

/* Light/dark theme toggle.
   Persists to localStorage ('soko-theme') and sets data-theme on <html>.
   A matching inline script in public/index.html applies the saved theme
   before first paint to avoid a flash. Also keeps the <meta name="theme-color">
   in sync so the mobile status bar matches. */

function readTheme() {
  try {
    return (
      document.documentElement.getAttribute('data-theme') ||
      localStorage.getItem('soko-theme') ||
      'light'
    );
  } catch (e) {
    return 'light';
  }
}

function SunIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
    </svg>
  );
}
function MoonIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" />
    </svg>
  );
}

/* Segmented Light / Dark pill — used on Settings › Preferences.
   Shares the same data-theme + localStorage mechanism as ThemeToggle. */
export function AppearancePills() {
  const [theme, setTheme] = useState(readTheme);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    try { localStorage.setItem('soko-theme', theme); } catch (e) {}
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute('content', theme === 'dark' ? '#0A2831' : '#0B3A4A');
  }, [theme]);

  return (
    <div className="appearance-pills" role="group" aria-label="Appearance">
      <button type="button" className={`appearance-pill ${theme !== 'dark' ? 'is-selected' : ''}`} onClick={() => setTheme('light')} aria-pressed={theme !== 'dark'}>
        <SunIcon /> Light
      </button>
      <button type="button" className={`appearance-pill ${theme === 'dark' ? 'is-selected' : ''}`} onClick={() => setTheme('dark')} aria-pressed={theme === 'dark'}>
        <MoonIcon /> Dark
      </button>
    </div>
  );
}

export default function ThemeToggle({ withLabel = false, className = '' }) {
  const [theme, setTheme] = useState(readTheme);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    try { localStorage.setItem('soko-theme', theme); } catch (e) {}
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute('content', theme === 'dark' ? '#0A2831' : '#0B3A4A');
  }, [theme]);

  const dark = theme === 'dark';
  const label = dark ? 'Light mode' : 'Dark mode';

  return (
    <button
      type="button"
      className={`theme-toggle ${className}`}
      onClick={() => setTheme(dark ? 'light' : 'dark')}
      aria-label={dark ? 'Switch to light mode' : 'Switch to dark mode'}
      title={label}
    >
      {dark ? <SunIcon /> : <MoonIcon />}
      {withLabel && <span className="theme-toggle__label">{label}</span>}
    </button>
  );
}
